/**
 * What a response from this app's API means.
 *
 * Separate from `api.ts` on purpose. `api.ts` builds the authenticated client
 * and therefore imports `../firebase`; every page module's test mocks `./api`
 * with a factory that REPLACES the module, so anything living there is
 * `undefined` inside a test. A pure helper with no dependencies belongs
 * somewhere the tests do not have to mock at all.
 */

/**
 * The JSON body of a response, or an Error carrying the server's own message.
 * `detail` is FastAPI's error shape, and it says more than a bare status (e.g.
 * "Venue and city are required."). Shared by every page module. (2026-09-11)
 */
export async function orThrow(
  res: Response,
  fallback: string,
): Promise<unknown> {
  if (res.ok) return res.status === 204 ? null : await res.json();
  let detail: string | undefined;
  try {
    const body = (await res.json()) as { detail?: unknown };
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    // Non-JSON error body. Fall through to the status-code message rather than
    // masking the original failure with a parse error.
  }
  throw new Error(detail ?? `${fallback} (HTTP ${res.status})`);
}
